import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { serviceService } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Wrench',
    image: '',
    popular: false,
    packages: [{ name: '', description: '', duration: '' }]
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await serviceService.getAll();
      setServices(data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: 'Error',
        description: 'Failed to load services',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPackage = () => {
    setFormData({
      ...formData,
      packages: [...formData.packages, { name: '', description: '', duration: '' }]
    });
  };

  const handleRemovePackage = (index) => {
    const newPackages = formData.packages.filter((_, i) => i !== index);
    setFormData({ ...formData, packages: newPackages });
  };

  const handlePackageChange = (index, field, value) => {
    const newPackages = [...formData.packages];
    newPackages[index][field] = value;
    setFormData({ ...formData, packages: newPackages });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'Wrench',
      image: '',
      popular: false,
      packages: [{ name: '', description: '', duration: '' }]
    });
    setEditingService(null);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      icon: service.icon,
      image: service.image,
      popular: service.popular,
      packages: service.packages
    });
    setIsAddDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || formData.packages.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      if (editingService) {
        // Update service
        await serviceService.update(editingService.id, formData);
        toast({
          title: 'Success',
          description: 'Service updated successfully'
        });
      } else {
        // Create service
        await serviceService.create(formData);
        toast({
          title: 'Success',
          description: 'Service created successfully'
        });
      }
      
      setIsAddDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: 'Error',
        description: error.detail || 'Failed to save service',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await serviceService.delete(serviceId);
      toast({
        title: 'Success',
        description: 'Service deleted successfully'
      });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: 'Error',
        description: error.detail || 'Failed to delete service',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading services...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Service Categories</span>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingService ? 'Edit Service' : 'Add New Service'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Service Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Periodic Services"
                        required
                      />
                    </div>
                    <div>
                      <Label>Icon Name</Label>
                      <Input
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        placeholder="e.g., Wrench"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Description *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Service description"
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <Label>Image URL</Label>
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.popular}
                      onCheckedChange={(checked) => setFormData({ ...formData, popular: checked })}
                    />
                    <Label>Mark as Popular</Label>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Packages *</Label>
                      <Button type="button" onClick={handleAddPackage} size="sm" variant="outline">
                        <Plus className="w-4 h-4 mr-1" /> Add Package
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {formData.packages.map((pkg, index) => (
                        <Card key={index} className="p-4">
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                placeholder="Package name"
                                value={pkg.name}
                                onChange={(e) => handlePackageChange(index, 'name', e.target.value)}
                              />
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Duration (e.g., 2-3 hours)"
                                  value={pkg.duration}
                                  onChange={(e) => handlePackageChange(index, 'duration', e.target.value)}
                                />
                                {formData.packages.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleRemovePackage(index)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <Textarea
                              placeholder="Package description"
                              value={pkg.description}
                              onChange={(e) => handlePackageChange(index, 'description', e.target.value)}
                              rows={2}
                            />
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="w-4 h-4 mr-2" /> Save Service
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <Card key={service.id} className="hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  <div className="h-32 mb-3 rounded-lg overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-lg">{service.name}</h3>
                    {service.popular && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Popular</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{service.description}</p>
                  <div className="text-sm text-gray-700 mb-3">
                    <strong>{service.packages?.length || 0}</strong> packages
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(service)}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(service.id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServicesManager;
